import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Grid,
  GridItem,
  Checkbox,
} from '@chakra-ui/react';

import DateField from 'components/fields/DateField';
import {
  parseDateString,
  formatDateToString,
} from 'components/fields/auto/resolvers/resolverUtils';
import type { Trailer } from 'api/quote-marketplace';
import { useValidationSchema } from '../hooks';

export interface TrailerModalProps {
  isOpen: boolean;
  onClose: () => void;
  trailer: Trailer;
  onChange: (trailer: Trailer) => void;
  onSave: () => void;
  trailerTypeOptions: { value: string; label: string }[];
  conditionOptions: { value: string; label: string }[];
  roadLicensedOptions: { value: string; label: string }[];
  provinceOptions: { value: string; label: string }[];
  occupiedByOwnerOptions: { value: string; label: string }[];
  permanentlyParkedOptions: { value: string; label: string }[];
  hailCoverageOptions: { value: string; label: string }[];
  title?: string;
}

const TrailerModal = ({
  isOpen,
  onClose,
  trailer,
  onChange,
  onSave,
  trailerTypeOptions,
  conditionOptions,
  roadLicensedOptions,
  provinceOptions,
  occupiedByOwnerOptions,
  permanentlyParkedOptions,
  hailCoverageOptions,
  title = 'Trailer',
}: TrailerModalProps) => {
  const { isFieldVisible } = useValidationSchema();
  const trailerEntity = trailer as unknown as Record<string, unknown>;

  const showOtherTrailerType = isFieldVisible(
    'trailer',
    'other_trailer_type',
    trailerEntity,
  );
  const showRoadLicensed = isFieldVisible(
    'trailer',
    'is_the_trailer_road_licensed',
    trailerEntity,
  );
  const showProvince = isFieldVisible(
    'trailer',
    'province_territory',
    trailerEntity,
  );
  const showOccupiedByOwner = isFieldVisible(
    'trailer',
    'occupied_by_the_owner',
    trailerEntity,
  );
  const showPermanentlyParked = isFieldVisible(
    'trailer',
    'the_trailer_is_permanently_parked',
    trailerEntity,
  );
  const showParkName = isFieldVisible(
    'trailer',
    'park_name_if_applicable',
    trailerEntity,
  );
  const showEquipmentValue = isFieldVisible(
    'trailer',
    'equipment_and_contents_value_cad',
    trailerEntity,
  );
  const showHailCoverage = isFieldVisible(
    'trailer',
    'hail_coverage_required',
    trailerEntity,
  );
  const showDaysOutside = isFieldVisible(
    'trailer',
    'number_of_days_outside_of_canada_per_annum',
    trailerEntity,
  );
  const showWeeksRented = isFieldVisible(
    'trailer',
    'number_of_weeks_rented_to_others_per_annum',
    trailerEntity,
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalOverlay />
      <ModalContent maxH="90vh" overflowY="auto">
        <ModalHeader>
          {trailer.id ? `Edit ${title}` : `Add ${title}`}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Grid templateColumns="repeat(2, 1fr)" gap={4}>
            <GridItem colSpan={2}>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Trailer Type</FormLabel>
                <Select
                  variant="main"
                  size="sm"
                  value={trailer.trailer_type}
                  onChange={(e) =>
                    onChange({
                      ...trailer,
                      trailer_type: e.target.value,
                    })
                  }
                >
                  {trailerTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </GridItem>

            {showOtherTrailerType && (
              <GridItem colSpan={2}>
                <FormControl>
                  <FormLabel fontSize="sm">Other Trailer Type</FormLabel>
                  <Input
                    variant="main"
                    size="sm"
                    value={trailer.other_trailer_type}
                    onChange={(e) =>
                      onChange({
                        ...trailer,
                        other_trailer_type: e.target.value,
                      })
                    }
                    placeholder="Describe trailer type"
                  />
                </FormControl>
              </GridItem>
            )}

            <GridItem>
              <FormControl>
                <FormLabel fontSize="sm">Manufacturer</FormLabel>
                <Input
                  variant="main"
                  size="sm"
                  value={trailer.manufacturer}
                  onChange={(e) =>
                    onChange({
                      ...trailer,
                      manufacturer: e.target.value,
                    })
                  }
                  placeholder="Manufacturer"
                />
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel fontSize="sm">Make/Model</FormLabel>
                <Input
                  variant="main"
                  size="sm"
                  value={trailer.make_model}
                  onChange={(e) =>
                    onChange({
                      ...trailer,
                      make_model: e.target.value,
                    })
                  }
                  placeholder="Make and model"
                />
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel fontSize="sm">Model Year</FormLabel>
                <Input
                  variant="main"
                  size="sm"
                  type="number"
                  value={trailer.model_year_yyyy ?? ''}
                  onChange={(e) =>
                    onChange({
                      ...trailer,
                      model_year_yyyy: e.target.value
                        ? Number(e.target.value)
                        : null,
                    })
                  }
                  placeholder="YYYY"
                />
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel fontSize="sm">Length (ft)</FormLabel>
                <Input
                  variant="main"
                  size="sm"
                  type="number"
                  value={trailer.length_ft ?? ''}
                  onChange={(e) =>
                    onChange({
                      ...trailer,
                      length_ft: e.target.value ? Number(e.target.value) : null,
                    })
                  }
                  placeholder="Length in feet"
                />
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel fontSize="sm">Replacement Value (CAD)</FormLabel>
                <Input
                  variant="main"
                  size="sm"
                  type="number"
                  value={trailer.replacement_value_cad ?? ''}
                  onChange={(e) =>
                    onChange({
                      ...trailer,
                      replacement_value_cad: e.target.value
                        ? Number(e.target.value)
                        : null,
                    })
                  }
                />
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel fontSize="sm">License Plate</FormLabel>
                <Input
                  variant="main"
                  size="sm"
                  value={trailer.license_plate}
                  onChange={(e) =>
                    onChange({
                      ...trailer,
                      license_plate: e.target.value,
                    })
                  }
                />
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel fontSize="sm">Serial Number</FormLabel>
                <Input
                  variant="main"
                  size="sm"
                  value={trailer.identification_serial_number}
                  onChange={(e) =>
                    onChange({
                      ...trailer,
                      identification_serial_number: e.target.value,
                    })
                  }
                />
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel fontSize="sm">Condition When Purchased</FormLabel>
                <Select
                  variant="main"
                  size="sm"
                  value={trailer.condition_when_purchased}
                  onChange={(e) =>
                    onChange({
                      ...trailer,
                      condition_when_purchased: e.target.value,
                    })
                  }
                  placeholder="Select condition"
                >
                  {conditionOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </GridItem>
            <GridItem>
              <DateField
                label="Purchase Date"
                date={parseDateString(trailer.purchase_date || '')}
                onChange={(date: Date | null) =>
                  onChange({
                    ...trailer,
                    purchase_date: formatDateToString(date),
                  })
                }
                size="sm"
              />
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel fontSize="sm">Number of Operators</FormLabel>
                <Input
                  variant="main"
                  size="sm"
                  type="number"
                  value={trailer.number_of_operators ?? ''}
                  onChange={(e) =>
                    onChange({
                      ...trailer,
                      number_of_operators: e.target.value
                        ? Number(e.target.value)
                        : null,
                    })
                  }
                />
              </FormControl>
            </GridItem>

            {showRoadLicensed && (
              <GridItem>
                <FormControl>
                  <FormLabel fontSize="sm">Road Licensed</FormLabel>
                  <Select
                    variant="main"
                    size="sm"
                    value={trailer.is_the_trailer_road_licensed ? 'Yes' : 'No'}
                    onChange={(e) =>
                      onChange({
                        ...trailer,
                        is_the_trailer_road_licensed: e.target.value === 'Yes',
                      })
                    }
                  >
                    {roadLicensedOptions.map((option) => (
                      <option key={option.value} value={option.label}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </GridItem>
            )}

            {showProvince && (
              <GridItem>
                <FormControl>
                  <FormLabel fontSize="sm">Province/Territory</FormLabel>
                  <Select
                    variant="main"
                    size="sm"
                    value={trailer.province_territory}
                    onChange={(e) =>
                      onChange({
                        ...trailer,
                        province_territory: e.target.value,
                      })
                    }
                    placeholder="Select province"
                  >
                    {provinceOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </GridItem>
            )}

            {showOccupiedByOwner && (
              <GridItem>
                <FormControl>
                  <FormLabel fontSize="sm">Occupied by Owner</FormLabel>
                  <Select
                    variant="main"
                    size="sm"
                    value={trailer.occupied_by_the_owner}
                    onChange={(e) =>
                      onChange({
                        ...trailer,
                        occupied_by_the_owner: e.target.value,
                      })
                    }
                    placeholder="Select"
                  >
                    {occupiedByOwnerOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </GridItem>
            )}

            {showPermanentlyParked && (
              <GridItem>
                <FormControl>
                  <FormLabel fontSize="sm">Permanently Parked</FormLabel>
                  <Select
                    variant="main"
                    size="sm"
                    value={trailer.the_trailer_is_permanently_parked}
                    onChange={(e) =>
                      onChange({
                        ...trailer,
                        the_trailer_is_permanently_parked: e.target.value,
                      })
                    }
                    placeholder="Select"
                  >
                    {permanentlyParkedOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </GridItem>
            )}

            {showParkName && (
              <GridItem colSpan={2}>
                <FormControl>
                  <FormLabel fontSize="sm">Park Name</FormLabel>
                  <Input
                    variant="main"
                    size="sm"
                    value={trailer.park_name_if_applicable}
                    onChange={(e) =>
                      onChange({
                        ...trailer,
                        park_name_if_applicable: e.target.value,
                      })
                    }
                    placeholder="Park name"
                  />
                </FormControl>
              </GridItem>
            )}

            {showEquipmentValue && (
              <GridItem>
                <FormControl>
                  <FormLabel fontSize="sm">Equipment Value (CAD)</FormLabel>
                  <Input
                    variant="main"
                    size="sm"
                    type="number"
                    value={trailer.equipment_and_contents_value_cad ?? ''}
                    onChange={(e) =>
                      onChange({
                        ...trailer,
                        equipment_and_contents_value_cad: e.target.value
                          ? Number(e.target.value)
                          : null,
                      })
                    }
                  />
                </FormControl>
              </GridItem>
            )}

            {showHailCoverage && (
              <GridItem>
                <FormControl>
                  <FormLabel fontSize="sm">Hail Coverage Required</FormLabel>
                  <Select
                    variant="main"
                    size="sm"
                    value={trailer.hail_coverage_required}
                    onChange={(e) =>
                      onChange({
                        ...trailer,
                        hail_coverage_required: e.target.value,
                      })
                    }
                    placeholder="Select"
                  >
                    {hailCoverageOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </GridItem>
            )}

            {showDaysOutside && (
              <GridItem>
                <FormControl>
                  <FormLabel fontSize="sm">Days Outside Canada/Year</FormLabel>
                  <Input
                    variant="main"
                    size="sm"
                    type="number"
                    value={
                      trailer.number_of_days_outside_of_canada_per_annum ?? ''
                    }
                    onChange={(e) =>
                      onChange({
                        ...trailer,
                        number_of_days_outside_of_canada_per_annum: e.target
                          .value
                          ? Number(e.target.value)
                          : null,
                      })
                    }
                  />
                </FormControl>
              </GridItem>
            )}

            <GridItem colSpan={2}>
              <Checkbox
                isChecked={trailer.rented_or_leased_to_others}
                onChange={(e) =>
                  onChange({
                    ...trailer,
                    rented_or_leased_to_others: e.target.checked,
                  })
                }
              >
                Rented or Leased to Others
              </Checkbox>
            </GridItem>

            {showWeeksRented && (
              <GridItem>
                <FormControl>
                  <FormLabel fontSize="sm">Weeks Rented/Year</FormLabel>
                  <Input
                    variant="main"
                    size="sm"
                    type="number"
                    value={
                      trailer.number_of_weeks_rented_to_others_per_annum ?? ''
                    }
                    onChange={(e) =>
                      onChange({
                        ...trailer,
                        number_of_weeks_rented_to_others_per_annum: e.target
                          .value
                          ? Number(e.target.value)
                          : null,
                      })
                    }
                  />
                </FormControl>
              </GridItem>
            )}
          </Grid>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="brand" onClick={onSave}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default TrailerModal;
